import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { removeNotification } from "../../store/uiSlice";
import { Icon } from "../checkout/icons";

/**
 * Toast notification container.
 *
 * Previously this rendered emoji glyphs (✅ ❌ ⚠️ ℹ️ 📢) with hardcoded
 * hex colour swatches (#dcfce7, #fee2e2, …) — which:
 *   (a) read as "AI-demo" UI the same way the checkout emoji icons did, and
 *   (b) ignored per-store theme tokens entirely, so a Tayrona success toast
 *       looked identical to a Clean one.
 *
 * Now:
 *   - Icons come from the shared SVG registry (check-circle, alert-circle,
 *     info-circle, shopping-bag for "add-to-cart" success).
 *   - Colours read CSS variables set by each store's theme
 *     (--color-success / --color-error / --color-warning / --color-info /
 *     --color-bg / --color-bg-secondary / --color-border). Stores can
 *     recolour the whole toast system by updating theme.json tokens
 *     without touching any component code.
 *   - "Item added to cart" toasts carry a store-agnostic shopping-bag
 *     icon instead of a generic checkmark; the `meta.variant` field on
 *     the notification payload (optional) lets any store customise the
 *     icon per notification source without forking the component.
 */

type NotificationType = "success" | "error" | "warning" | "info";

interface VariantConfig {
  icon: string;
  bg: string;
  border: string;
  text: string;
}

const VARIANT: Record<NotificationType, VariantConfig> = {
  success: {
    icon: "check-circle",
    bg: "var(--color-success-bg, rgba(22, 163, 74, 0.08))",
    border: "var(--color-success, #16a34a)",
    text: "var(--color-success, #15803d)",
  },
  error: {
    icon: "alert-circle",
    bg: "var(--color-error-bg, rgba(220, 38, 38, 0.08))",
    border: "var(--color-error, #dc2626)",
    text: "var(--color-error, #b91c1c)",
  },
  warning: {
    icon: "alert-circle",
    bg: "var(--color-warning-bg, rgba(217, 119, 6, 0.1))",
    border: "var(--color-warning, #d97706)",
    text: "var(--color-warning, #b45309)",
  },
  info: {
    icon: "info-circle",
    bg: "var(--color-info-bg, rgba(37, 99, 235, 0.08))",
    border: "var(--color-info, #2563eb)",
    text: "var(--color-info, #1d4ed8)",
  },
};

function pickVariant(type: string): VariantConfig {
  return VARIANT[type as NotificationType] ?? VARIANT.info;
}

function iconForNotification(notification: {
  type: string;
  message?: string;
  meta?: { icon?: string; variant?: string };
}): string {
  if (notification.meta?.icon) return notification.meta.icon;
  // Heuristic: cart-add messages get a shopping-bag icon regardless of
  // variant, so the user sees the intent at a glance even before reading.
  const msg = (notification.message || "").toLowerCase();
  if (msg.includes("added to cart") || msg.includes("cart updated")) {
    return "shopping-bag";
  }
  return pickVariant(notification.type).icon;
}

const NotificationContainer: React.FC = () => {
  const dispatch = useDispatch();
  const notifications = useSelector((state: RootState) => state.ui.notifications);

  useEffect(() => {
    notifications.forEach((notification) => {
      if (notification.duration) {
        const timer = setTimeout(() => {
          dispatch(removeNotification(notification.id));
        }, notification.duration);
        return () => clearTimeout(timer);
      }
    });
  }, [notifications, dispatch]);

  if (notifications.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "calc(var(--header-height, 80px) + 16px)",
        right: "16px",
        zIndex: 1001,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
      data-testid="notifications"
    >
      {notifications.map((notification) => {
        const variant = pickVariant(notification.type);
        const iconName = iconForNotification(notification as any);
        return (
          <div
            key={notification.id}
            role="status"
            data-testid={`notification-${notification.type}`}
            data-notification-icon={iconName}
            style={{
              background: variant.bg,
              color: variant.text,
              padding: "12px 16px",
              borderRadius: "var(--radius-md, 8px)",
              boxShadow: "var(--shadow-sm, 0 4px 12px rgba(0, 0, 0, 0.08))",
              border: `1px solid ${variant.border}`,
              minWidth: "280px",
              maxWidth: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              fontFamily: "var(--font-family-body, inherit)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                minWidth: 0,
              }}
            >
              <span
                aria-hidden="true"
                style={{ color: variant.border, display: "inline-flex", flexShrink: 0 }}
              >
                <Icon name={iconName} size={20} />
              </span>
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  lineHeight: 1.35,
                }}
              >
                {notification.message}
              </span>
            </div>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dispatch(removeNotification(notification.id))}
              style={{
                background: "none",
                border: "none",
                color: "inherit",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "4px",
                opacity: 0.6,
                display: "inline-flex",
                flexShrink: 0,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = "0.6";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationContainer;
